import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronRight, ChevronLeft, SkipForward } from 'lucide-react';
import { createPortal } from 'react-dom';

interface TourStep {
  target: string;
  title: string;
  content: string;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'center';
}

interface AppTourProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenSidebar: () => void;
  onCloseSidebar: () => void;
  onNavigateHome?: () => void;
  language?: 'hi' | 'en';
}

const tourSteps: TourStep[] = [
  // Welcome
  {
    target: 'body',
    title: '🙏 गोलोक में आपका स्वागत है!',
    content: 'आइए आपको app की सैर कराते हैं। सिर्फ 2 मिनट!',
    placement: 'center',
  },
  // Jaap Area (Main interaction area - always visible)
  {
    target: '[data-testid="jaap-stats"]',
    title: '📿 जाप Area',
    content: 'यहाँ राम नाम जपें! लिखित, स्मरण, या वाणी mode चुनें। हर जाप count होगा!',
    placement: 'bottom',
  },
  // Mala Counter
  {
    target: '[data-testid="mala-counter"]',
    title: '🔢 माला Progress',
    content: '108 जाप = 1 माला। यहाँ progress देखें!',
    placement: 'bottom',
  },
  // Menu Button
  {
    target: '[data-testid="menu-button"]',
    title: '☰ Menu',
    content: 'यहाँ से सभी features access करें। चलो menu देखते हैं!',
    placement: 'bottom',
  },
  // Sidebar items (will auto-open sidebar) - using correct IDs from DEFAULT_SIDEBAR_MENU
  {
    target: '[data-testid="sidebar-ai_vani"]',
    title: '✨ AI Sant Vani',
    content: 'संतों की वाणी में AI से सवाल पूछें और दिव्य ज्ञान पाएं!',
    placement: 'right',
  },
  {
    target: '[data-testid="sidebar-ebooks"]',
    title: '📖 E-Books',
    content: 'दिव्य पुस्तकें - Ramcharitmanas, Bhagavad Gita और बहुत कुछ!',
    placement: 'right',
  },
  {
    target: '[data-testid="sidebar-audios"]',
    title: '🎵 Satsang Audio',
    content: 'भजन, कीर्तन और सत्संग सुनें',
    placement: 'right',
  },
  {
    target: '[data-testid="sidebar-videos"]',
    title: '🎬 Divine Videos',
    content: 'Satsang videos और spiritual content देखें',
    placement: 'right',
  },
  {
    target: '[data-testid="sidebar-poems"]',
    title: '📜 काव्य (Poems)',
    content: 'भक्ति कविताएं और दोहे पढ़ें',
    placement: 'right',
  },
  {
    target: '[data-testid="sidebar-scanner"]',
    title: '📸 Nidhi OCR',
    content: 'हाथ से लिखे राम नाम की photo लें - AI count करेगा!',
    placement: 'right',
  },
  {
    target: '[data-testid="sidebar-sangha"]',
    title: '👥 Sangha',
    content: 'Community के साथ मिलकर अखंड जाप करें!',
    placement: 'right',
  },
  {
    target: '[data-testid="sidebar-about"]',
    title: '📋 About & Contact',
    content: 'Golok के बारे में जानें और हमसे संपर्क करें',
    placement: 'right',
  },
  // Final
  {
    target: 'body',
    title: '🎉 Tour पूरा!',
    content: 'जय श्री राम! अब जाप शुरू करें और पुण्य कमाएं! 🙏',
    placement: 'center',
  },
];

export const AppTour: React.FC<AppTourProps> = ({
  isOpen,
  onClose,
  onOpenSidebar,
  onCloseSidebar,
  onNavigateHome,
  language = 'hi',
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [sidebarOpened, setSidebarOpened] = useState(false);

  // Navigate to home when tour opens (so Jaap Counter is visible)
  useEffect(() => {
    if (isOpen && onNavigateHome) {
      onNavigateHome();
    }
  }, [isOpen, onNavigateHome]);

  const step = tourSteps[currentStep];

  // Find and highlight target element
  useEffect(() => {
    if (!isOpen) return;

    const findTarget = () => {
      if (step.placement === 'center') {
        setTargetRect(null);
        return;
      }

      const element = document.querySelector(step.target);
      if (element) {
        const rect = element.getBoundingClientRect();
        setTargetRect(rect);
        // Scroll element into view
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        console.log(`Tour: Element not found for target: ${step.target}`);
        setTargetRect(null);
      }
    };

    // Delay to allow navigation and render to complete
    const timer = setTimeout(findTarget, 300);
    return () => clearTimeout(timer);
  }, [isOpen, currentStep, step]);

  // Handle sidebar opening for sidebar steps (steps 4-11 are sidebar items)
  const SIDEBAR_START = 4;
  const SIDEBAR_END = 11;
  const FINAL_STEP = tourSteps.length - 1;

  useEffect(() => {
    if (!isOpen) return;

    // Open sidebar for sidebar item steps
    if (currentStep >= SIDEBAR_START && currentStep <= SIDEBAR_END && !sidebarOpened) {
      onOpenSidebar();
      setSidebarOpened(true);
    }

    // Close sidebar at the final step
    if (currentStep === FINAL_STEP && sidebarOpened) {
      onCloseSidebar();
      setSidebarOpened(false);
    }

    // Close sidebar if going back before sidebar steps
    if (currentStep < SIDEBAR_START && sidebarOpened) {
      onCloseSidebar();
      setSidebarOpened(false);
    }
  }, [isOpen, currentStep, sidebarOpened, onOpenSidebar, onCloseSidebar]);

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleClose();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = useCallback(() => {
    setCurrentStep(0);
    setSidebarOpened(false);
    onCloseSidebar();
    localStorage.setItem('app_tour_completed', 'true');
    onClose();
  }, [onClose, onCloseSidebar]);

  const handleSkip = () => {
    handleClose();
  };

  if (!isOpen) return null;

  // Calculate tooltip position - ensures tooltip stays within viewport
  const getTooltipStyle = (): React.CSSProperties => {
    if (!targetRect || step.placement === 'center') {
      return {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      };
    }

    const padding = 16;
    const tooltipWidth = 300;
    const tooltipHeight = 220; // Slightly increased for button visibility
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Helper to clamp top position within viewport
    const clampTop = (top: number) => {
      return Math.max(padding, Math.min(top, viewportHeight - tooltipHeight - padding));
    };

    // Helper to clamp left position within viewport
    const clampLeft = (left: number) => {
      return Math.max(padding, Math.min(left, viewportWidth - tooltipWidth - padding));
    };

    switch (step.placement) {
      case 'bottom':
        return {
          position: 'fixed',
          top: clampTop(targetRect.bottom + padding),
          left: clampLeft(targetRect.left + targetRect.width / 2 - tooltipWidth / 2),
        };
      case 'top':
        return {
          position: 'fixed',
          top: clampTop(targetRect.top - tooltipHeight - padding),
          left: clampLeft(targetRect.left + targetRect.width / 2 - tooltipWidth / 2),
        };
      case 'right':
        // For sidebar items - center tooltip vertically relative to target, clamped to viewport
        const rightTop = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
        return {
          position: 'fixed',
          top: clampTop(rightTop),
          left: clampLeft(targetRect.right + padding),
        };
      case 'left':
        const leftTop = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
        return {
          position: 'fixed',
          top: clampTop(leftTop),
          left: Math.max(padding, targetRect.left - tooltipWidth - padding),
        };
      default:
        return {
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        };
    }
  };

  // Spotlight cutout style - creates a "hole" to show the target element
  const getSpotlightStyle = (): React.CSSProperties => {
    if (!targetRect || step.placement === 'center') {
      return {};
    }

    const padding = 12; // More padding around target
    return {
      position: 'fixed',
      top: targetRect.top - padding,
      left: targetRect.left - padding,
      width: targetRect.width + padding * 2,
      height: targetRect.height + padding * 2,
      borderRadius: 12,
      border: '3px solid #FF6B00', // Orange border to highlight
      backgroundColor: 'transparent',
      pointerEvents: 'none' as const,
      zIndex: 9998,
      animation: 'pulse-border 2s infinite',
    };
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999]">
      {/* Add pulse animation style */}
      <style>{`
        @keyframes pulse-border {
          0%, 100% { box-shadow: 0 0 0 0 rgba(255, 107, 0, 0.4), 0 0 0 9999px rgba(0, 0, 0, 0.5); }
          50% { box-shadow: 0 0 0 8px rgba(255, 107, 0, 0.2), 0 0 0 9999px rgba(0, 0, 0, 0.5); }
        }
      `}</style>
      
      {/* Base overlay - always shown */}
      <div 
        className="absolute inset-0 bg-black/50 transition-opacity duration-300"
        onClick={handleSkip}
      />
      
      {/* Spotlight cutout - only when target exists and not center */}
      {targetRect && step.placement !== 'center' && (
        <div style={getSpotlightStyle()} />
      )}

      {/* Tooltip */}
      <div
        style={getTooltipStyle()}
        className="bg-white dark:bg-stone-800 rounded-2xl shadow-2xl p-5 max-w-[320px] z-[10000] animate-fade-in border-2 border-saffron/30"
      >
        {/* Arrow pointer - shows which element is being described */}
        {targetRect && step.placement !== 'center' && (
          <div 
            className={`absolute w-4 h-4 bg-white dark:bg-stone-800 border-saffron/30 transform rotate-45 ${
              step.placement === 'bottom' ? '-top-2 left-1/2 -translate-x-1/2 border-l-2 border-t-2' :
              step.placement === 'top' ? '-bottom-2 left-1/2 -translate-x-1/2 border-r-2 border-b-2' :
              step.placement === 'right' ? '-left-2 top-6 border-l-2 border-b-2' :
              step.placement === 'left' ? '-right-2 top-6 border-r-2 border-t-2' : ''
            }`}
          />
        )}
        
        {/* Close button */}
        <button
          onClick={handleSkip}
          className="absolute top-3 right-3 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-stone-700 transition-colors"
        >
          <X size={18} className="text-gray-400" />
        </button>

        {/* Content */}
        <div className="mb-5">
          <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-2">
            {step.title}
          </h3>
          <p className="text-sm text-gray-600 dark:text-stone-300 leading-relaxed">
            {step.content}
          </p>
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-1.5 mb-4">
          {tourSteps.map((_, idx) => (
            <div
              key={idx}
              className={`w-2 h-2 rounded-full transition-colors ${
                idx === currentStep
                  ? 'bg-saffron'
                  : idx < currentStep
                  ? 'bg-green-400'
                  : 'bg-gray-300 dark:bg-stone-600'
              }`}
            />
          ))}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <button
            onClick={handleSkip}
            className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-stone-300 transition-colors"
          >
            Skip Tour
          </button>

          <div className="flex items-center gap-2">
            {currentStep > 0 && (
              <button
                onClick={handlePrev}
                className="p-2 rounded-xl bg-gray-100 dark:bg-stone-700 hover:bg-gray-200 dark:hover:bg-stone-600 transition-colors"
              >
                <ChevronLeft size={18} />
              </button>
            )}
            <button
              onClick={handleNext}
              className="px-4 py-2 rounded-xl bg-saffron text-white font-bold text-sm hover:bg-orange-600 transition-colors flex items-center gap-1"
            >
              {currentStep === tourSteps.length - 1 ? (
                'समाप्त'
              ) : (
                <>
                  आगे <ChevronRight size={16} />
                </>
              )}
            </button>
          </div>
        </div>

        {/* Step counter */}
        <div className="text-center mt-3">
          <span className="text-xs text-gray-400">
            {currentStep + 1} / {tourSteps.length}
          </span>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Hook to check if tour should be shown
export const useAppTour = () => {
  const [shouldShowTour, setShouldShowTour] = useState(false);

  useEffect(() => {
    // Check if tour was completed before
    const tourCompleted = localStorage.getItem('app_tour_completed');
    if (tourCompleted) return;

    // Check if PWA standalone mode
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    
    if (isStandalone) {
      // For PWA: Check if this is first PWA open (separate from browser session)
      const isFirstPWAOpen = !localStorage.getItem('app_first_open_done');
      if (isFirstPWAOpen) {
        localStorage.setItem('app_first_open_done', 'true');
        setShouldShowTour(true);
      }
    } else {
      // For browser: Check if first browser session
      const isFirstSession = !localStorage.getItem('app_tour_first_session');
      if (isFirstSession) {
        localStorage.setItem('app_tour_first_session', 'true');
        setShouldShowTour(true);
      }
    }
  }, []);

  const triggerTour = () => setShouldShowTour(true);
  const closeTour = () => setShouldShowTour(false);
  const resetTour = () => {
    localStorage.removeItem('app_tour_completed');
    localStorage.removeItem('app_first_open_done');
    localStorage.removeItem('app_tour_first_session');
    setShouldShowTour(true);
  };

  return { shouldShowTour, triggerTour, closeTour, resetTour };
};

export default AppTour;
